import styled from 'styled-components'
import { PublicProgram } from 'src/types/Affiliate'
import { formatBps, formatCents } from 'src/utils/affiliateFormat'

// The affiliate terms text. Its version is AffiliateProgram.termsVersion on the API: when this text changes
// materially, bump termsVersion (admin → Program settings) so every affiliate accepts the new version.

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.85);

    h3 {
        font-weight: 700;
        color: white;
        margin-top: 8px;
    }

    ul {
        list-style: disc;
        padding-left: 20px;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
`

const TermsContent = ({ program }: { program?: PublicProgram }) => {
    const first = program ? formatBps(program.firstMonthBps) : 'the first-month rate'
    const recurring = program ? formatBps(program.recurringBps) : 'the recurring rate'
    const holdDays = program ? program.holdDays : 30
    const minimum = program ? formatCents(program.minPayoutCents) : 'the minimum'

    return (
        <Body>
            <p>
                These terms apply to everyone in the SwiftSole affiliate program. By accepting them you agree to
                everything below, in addition to the SwiftSole Terms of Service.
            </p>

            <h3>1. Commissions</h3>
            <ul>
                <li>
                    You earn a commission when someone you refer subscribes to SwiftSole through your link or code: {first}{' '}
                    of their first paid month and {recurring} of every paid month after that, for as long as they stay
                    subscribed. Some affiliates have individually agreed rates, which are shown on your Affiliate tab.
                </li>
                <li>
                    Commissions are calculated on the subscription price actually paid, after discounts. One-time setup
                    fees, taxes and amounts paid with account credit don't count.
                </li>
                <li>
                    Only new customers count. A person who has paid for SwiftSole before isn't a new referral.
                </li>
                <li>
                    We can change rates at any time. A change only applies to payments made after it; commissions you've
                    already earned keep the rate they were earned at.
                </li>
            </ul>

            <h3>2. Hold period and payouts</h3>
            <ul>
                <li>
                    Each commission becomes available {holdDays} days after the referred payment, so refunds and
                    chargebacks can settle first.
                </li>
                <li>
                    You can request a payout once your available balance reaches {minimum} (or your individually agreed
                    minimum). A payout always withdraws your full available balance.
                </li>
                <li>
                    Payouts are sent through Stripe Connect. You need a Stripe account in good standing, and Stripe's own
                    terms apply to it. We aren't responsible for delays or fees on Stripe's side.
                </li>
                <li>
                    You're responsible for any taxes on your earnings. Stripe may collect tax information from you and
                    issue tax forms (such as a 1099) on our behalf.
                </li>
            </ul>

            <h3>3. Refunds and chargebacks</h3>
            <ul>
                <li>
                    If a referred payment is refunded or disputed, the matching commission is reduced or reversed. If it
                    was already paid out, the amount is deducted from your future earnings, and your balance can go
                    negative until it is offset.
                </li>
                <li>If a dispute is later resolved in our favour, the commission is restored.</li>
            </ul>

            <h3>4. Fair play</h3>
            <ul>
                <li>
                    You can't refer yourself, or earn commissions on accounts you own or control, including accounts that
                    use your payment details.
                </li>
                <li>
                    We review referrals that look suspicious. Commissions under review are withheld until the review
                    finishes, and commissions we find to be fraudulent are cancelled.
                </li>
                <li>
                    Don't bid on "SwiftSole" or similar brand terms in paid search ads, and don't run ads that look like
                    they come from SwiftSole.
                </li>
                <li>
                    Don't spam, and don't make false or misleading claims about SwiftSole, its features, pricing or
                    results.
                </li>
            </ul>

            <h3>5. Pausing and termination</h3>
            <ul>
                <li>
                    We can pause or end your participation at any time. While paused, you keep earning on customers you
                    referred before the pause, but your link stops crediting new referrals and you can't request
                    payouts.
                </li>
                <li>
                    If we end your participation for fraud or a serious breach of these terms, any unpaid balance is
                    forfeited. Otherwise we pay out your remaining balance once it clears the hold period.
                </li>
            </ul>

            <h3>6. Changes to these terms</h3>
            <ul>
                <li>
                    We may update these terms. When we do, you'll be asked to accept the new version before you can request
                    payouts or change your settings. Commissions keep accruing in the meantime.
                </li>
            </ul>
        </Body>
    )
}

export default TermsContent
